import React, {useContext, useState, useCallback, useEffect, useRef} from 'react';
import {Button, setOptions, toast} from "@mobiscroll/react";
import '@mobiscroll/react/dist/css/mobiscroll.min.css';
import '../styles/dashboardWorkspaceCode.css';
import {languages} from '@codemirror/language-data';
import {EditorView, lineNumbers, keymap} from '@codemirror/view';
import {EditorState} from '@codemirror/state';
import {MergeView} from '@codemirror/merge';
import {foldGutter, foldKeymap, LanguageDescription} from '@codemirror/language';
import {lintGutter} from '@codemirror/lint';
import {defaultKeymap} from '@codemirror/commands';
import {oneDark} from '@codemirror/theme-one-dark';
import Empty from "../utils/Empty";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faDownToLine, faCode, faUpToLine, faXmark, faCopy, faPipe} from "@fortawesome/pro-duotone-svg-icons";
import {NetworkContext} from "../utils/NetworkContext";

setOptions({
    theme: 'ios',
    themeVariant: 'light',
});

function DashboardWorkspaceCode({locale, workspaceItem, setLoading, teamtalk, navigation, ButtonBack, width}) {
    const {wsCall, wsResponse, setWsResponse} = useContext(NetworkContext);
    const bottomRef = useRef(null);
    const topRef = useRef(null);
    const containerRef = useRef(null);
    const editorViewRef = useRef(null);
    const [content, setContent] = useState(workspaceItem.data.content);
    const [iconHover, setIconHover] = useState(null);
    const broadcast = useRef(false);

    const CodeClose = useCallback(() => {
        ButtonBack();
    }, [ButtonBack]);

    const CodeTop = () => {
        topRef.current?.scrollIntoView({ behavior: 'smooth' });
    }

    const CodeBottom = () => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }

    const CodeCopy = async () => {
        try {
            await navigator.clipboard.writeText(content);

            toast({message: locale.dashboardWorkspaceCode.h, color: 'info', display: 'bottom', duration: 1000});
        } catch (error) {
            console.error('Failed to copy to clipboard:', error);
            toast({message: locale.dashboardWorkspaceCode.i, color: 'danger', display: 'bottom', duration: 3000});
        }
    }

    const EditorUpdate = data => {
        setContent(data);

        if (!broadcast.current && Empty(teamtalk) && data && data.length % 10 === 0) {

            EditorSave(data);
        } else if (!broadcast.current && !Empty(teamtalk) && data && data.length % 5 === 0) {

            EditorSave(data);
        } else if (broadcast.current) {

            broadcast.current = false;
        }
    }

    const EditorSave = data => {

        const message = {
            type: 'wss',
            path: 'text',
            action: 'update',
            data: {
                id: workspaceItem.id,
                content: data,
                teamtalk:  (!Empty(teamtalk) && teamtalk.id) ? {id: teamtalk.id, users: teamtalk.users} : null
            }
        };
        wsCall(message);
    }

    const ButtonUpdate = data => {
        setLoading(true);

        const message = {
            type: 'wss',
            path: 'workspace-item',
            action: 'update',
            data: {
                id: workspaceItem.id,
                teamtalk:  (!Empty(teamtalk) && teamtalk.id) ? {id: teamtalk.id, users: teamtalk.users} : null,
                tab: (!Empty(teamtalk) && teamtalk.id) ? null : navigation.tab
            }
        };
        if (data === 'accept') {
            message.data.accept = true;
        }
        if (data === 'reject') {
            message.data.reject = true;
        }
        wsCall(message);
    }

    const UpdateText = useCallback(data => {
        if (data.content){
            broadcast.current = true;
            setContent(data.content);
        }
    }, []);

    useEffect(() => {
        if (wsResponse &&
            !Empty(wsResponse) &&
            wsResponse.action === 'update' &&
            wsResponse.path === 'text' &&
            ((Empty(teamtalk) && !wsResponse.data.teamtalk) ||
                (!Empty(teamtalk) && wsResponse.data.teamtalk && teamtalk.id === wsResponse.data.teamtalk && wsResponse.data.id && wsResponse.data.id === workspaceItem.id))) {
            UpdateText(wsResponse.data);
            setWsResponse({});
        }
    }, [wsResponse, UpdateText, setWsResponse, teamtalk, workspaceItem]);

    useEffect(() => {
        const view = editorViewRef.current;
        if (!view) return;

        const currentContent = view.state.doc.toString();
        if (currentContent === content) return;

        view.dispatch({
            changes: {
                from: 0,
                to: view.state.doc.length,
                insert: content
            }
        });
    }, [content]);

    useEffect(() => {
        if (!containerRef.current) return;

        let cancelled = false;

        const initEditor = async () => {
            let languageExtension = [];
            if (workspaceItem.data.language && workspaceItem.data.language !== 'other') {
                const langDesc = LanguageDescription.matchLanguageName(
                    languages,
                    workspaceItem.data.language,
                    true
                );
                if (langDesc) {
                    const langSupport = await langDesc.load();
                    if (cancelled) return;
                    languageExtension = [langSupport];
                }
            }

            if (cancelled) return;

            const state = EditorState.create({
                doc: content,
                extensions: [
                    lineNumbers(),
                    foldGutter(),
                    lintGutter(),
                    oneDark,
                    ...languageExtension,
                    keymap.of([
                        ...defaultKeymap,
                        ...foldKeymap
                    ]),
                    EditorView.updateListener.of((update) => {
                        if (update.docChanged) {
                            EditorUpdate(update.state.doc.toString());
                        }
                    }),
                    EditorView.domEventHandlers({
                        blur: () => {
                            EditorSave(editorViewRef.current.state.doc.toString());
                            return false;
                        }
                    })
                ]
            });

            const view = workspaceItem.update
                ? new MergeView({
                    a: {
                        doc: content,
                        extensions: [lineNumbers(), ...languageExtension]
                    },
                    b: {
                        doc: workspaceItem.data.update.content,
                        extensions: [lineNumbers(), ...languageExtension]
                    },
                    parent: containerRef.current,
                    highlighting: true
                })
                : new EditorView({
                    state,
                    parent: containerRef.current
                });

            editorViewRef.current = view;
        };

        initEditor();

        return () => {
            cancelled = true;  // <-- signal the async function to stop
            if (editorViewRef.current) {
                editorViewRef.current.destroy();
                editorViewRef.current = null;
            }
        };
    }, []);

    return (
        <div className="cs-dashboard-workspace-code-fullscreen">
            <div className="mbsc-row cs-dashboard-workspace-code-header" >
                <div className="mbsc-col cs-dashboard-workspace-code-col-start" >
                    <Button color="secondary" variant="flat"  className="mbsc-bold cs-dashboard-workspace-code-color" >
                        <FontAwesomeIcon className="cs-dashboard-workspace-code-icon" icon={faCode} />
                        <div className="cs-dashboard-workspace-code-menu-text">
                            {workspaceItem.name}
                        </div>
                        {workspaceItem.data.language && workspaceItem.data.language !== 'other' && width > 768 &&
                            <>
                                <FontAwesomeIcon className="cs-dashboard-workspace-code-icon" icon={faPipe} />
                                <div className="cs-dashboard-workspace-code-menu-text">
                                    {workspaceItem.data.language}
                                </div>
                            </>
                        }
                    </Button>
                </div>
                <div className="mbsc-col-auto" >
                    <div className="mbsc-row cs-dashboard-workspace-code-col-end">
                        <Button onMouseEnter={() => setIconHover('copy')} onMouseLeave={() => setIconHover(null)} color="primary" variant="flat" className="mbsc-bold cs-dashboard-workspace-code-color" onClick={() => CodeCopy()} >
                            <FontAwesomeIcon className="cs-dashboard-workspace-code-icon" icon={faCopy} fade={iconHover === 'copy'} /><div className="cs-dashboard-workspace-code-menu-text">{locale.dashboardWorkspaceCode.g}</div>
                        </Button>
                        <Button onMouseEnter={() => setIconHover('bottom')} onMouseLeave={() => setIconHover(null)} color="primary" variant="flat" className="mbsc-bold cs-dashboard-workspace-code-color" onClick={() => CodeBottom()} >
                            <FontAwesomeIcon className="cs-dashboard-workspace-code-icon" icon={faDownToLine} fade={iconHover === 'bottom'} /><div className="cs-dashboard-workspace-code-menu-text">{locale.dashboardWorkspaceCode.f}</div>
                        </Button>
                        <Button onMouseEnter={() => setIconHover('top')} onMouseLeave={() => setIconHover(null)} color="primary" variant="flat" className="mbsc-bold cs-dashboard-workspace-code-color" onClick={() => CodeTop()} >
                            <FontAwesomeIcon className="cs-dashboard-workspace-code-icon" icon={faUpToLine} fade={iconHover === 'top'} /><div className="cs-dashboard-workspace-code-menu-text">{locale.dashboardWorkspaceCode.e}</div>
                        </Button>
                        <Button onMouseEnter={() => setIconHover('close')} onMouseLeave={() => setIconHover(null)} color="primary" variant="flat" className="mbsc-bold cs-dashboard-workspace-code-color" onClick={() => CodeClose()} >
                            <FontAwesomeIcon className="cs-dashboard-workspace-code-icon" icon={faXmark} fade={iconHover === 'close'} />
                        </Button>
                    </div>
                </div>
            </div>
            {workspaceItem.update &&
                <div className="mbsc-row cs-dashboard-workspace-code-header-secondary" >
                    <div className="mbsc-col" />
                    <div className="mbsc-col-auto" >
                        <div className="mbsc-row cs-dashboard-workspace-code-col-end">
                            <Button color="primary" variant="flat" className="mbsc-bold mbsc-txt-muted cs-dashboard-workspace-code-color" onClick={() => ButtonUpdate('accept')} >
                                {locale.dashboardWorkspaceCode.c}
                            </Button>
                            <Button color="primary" variant="flat" className="mbsc-bold mbsc-txt-muted cs-dashboard-workspace-code-color" onClick={() => ButtonUpdate('reject')} >
                                {locale.dashboardWorkspaceCode.d}
                            </Button>
                        </div>
                    </div>
                </div>
            }
            <div className="mbsc-row cs-dashboard-workspace-code-section-code">
                <div className="mbsc-col cs-dashboard-workspace-code-editor">
                    <div ref={topRef} />
                    <div ref={containerRef} className="cs-dashboard-workspace-code-editor-cm" />
                    <div ref={bottomRef} />
                </div>
            </div>
        </div>
    );
}

export default DashboardWorkspaceCode;
